import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Clock } from "lucide-react";
import api from "../lib/api";
import { FadeUp, Eyebrow, Marquee } from "../components/Reveal";
import { Waveform } from "../components/AudioArt";

export default function Services() {
  const [services, setServices] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locId, setLocId] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/services"), api.get("/locations")]).then(([s, l]) => {
      setServices(s.data);
      setLocations(l.data);
      if (l.data.length) setLocId(l.data[0].location_id);
    }).catch(() => {});
  }, []);

  const location = useMemo(() => locations.find((l) => l.location_id === locId), [locations, locId]);
  const price = (s) => Math.max(0, s.base_price + (location?.rate_adjustment || 0));

  return (
    <div className="bg-background">
      <section className="pt-20 pb-14 lg:pt-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <FadeUp>
            <Eyebrow>Services &amp; Rates</Eyebrow>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 tracking-tight">
              Choose your <span className="italic text-purple-700">finish</span>
            </h1>
            <p className="text-slate-500 mt-6 max-w-xl text-base leading-relaxed">
              Rates follow the room. Select a location and every price below adjusts instantly — no surprises at confirmation.
            </p>
          </FadeUp>

          <FadeUp delay={0.15} className="mt-12">
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-slate-400 mb-4">01 — Select location</p>
            <div className="flex flex-wrap gap-3" data-testid="location-selector">
              {locations.map((l) => (
                <button
                  key={l.location_id}
                  onClick={() => setLocId(l.location_id)}
                  data-testid={`location-option-${l.location_id}`}
                  className={`px-5 py-3 rounded-full text-sm border transition-all flex items-center gap-2 ${
                    locId === l.location_id
                      ? "bg-obsidian text-white border-obsidian shadow-lg"
                      : "bg-white text-slate-600 border-purple-100 hover:border-gold"
                  }`}
                >
                  <MapPin size={14} className={locId === l.location_id ? "text-gold" : "text-purple-400"} />
                  {l.name}
                  {l.rate_adjustment !== 0 && (
                    <span className={`text-xs font-mono ${locId === l.location_id ? "text-gold" : "text-slate-400"}`}>
                      {l.rate_adjustment > 0 ? `+$${l.rate_adjustment}` : `-$${Math.abs(l.rate_adjustment)}`}
                    </span>
                  )}
                </button>
              ))}
            </div>
            {location && <p className="text-sm text-slate-400 mt-4 italic font-serif">{location.description}</p>}
          </FadeUp>
        </div>
      </section>

      <section className="relative overflow-hidden bg-obsidian text-white grain" data-testid="services-photo-band">
        <div className="absolute inset-0 duotone-lilac">
          <img src="/empress-console-eq.webp" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F0E13] via-[#0F0E13]/60 to-transparent" />
        <Waveform className="absolute inset-x-0 top-1/2 -translate-y-1/2 w-full h-40 opacity-80 pointer-events-none" strands={4} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-20 lg:py-28 grid gap-8 lg:grid-cols-12 items-end">
          <FadeUp className="lg:col-span-7">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Inside Da Cave Studios</p>
            <p className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-4 leading-tight">Every rate buys the same thing: <span className="italic gold-gradient-text">Empress at the board.</span></p>
          </FadeUp>
          <FadeUp delay={0.15} className="lg:col-span-5 lg:justify-self-end">
            <div className="glass-dark rounded-2xl p-5 w-full max-w-xs">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">Lock it in</p>
              <p className="text-sm text-white/75 mt-2 leading-relaxed">Once Empress approves your request, a deposit locks your time. Packages carry a higher deposit so the project calendar stays yours.</p>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="pt-20 pb-24 lg:pb-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          {[
            { key: "individual", label: "02 — Individual rates", note: "" },
            { key: "deal", label: "03 — Deals", note: "2-hour special · monthly" },
            { key: "package", label: "04 — Project packages", note: "EP & album lock-ins" },
          ].map((g) => {
            const items = services.filter((s) => (s.category || "individual") === g.key);
            if (!items.length) return null;
            return (
              <div key={g.key} className="mt-16 first:mt-0">
                <p className="text-xs font-mono uppercase tracking-[0.25em] text-slate-400">
                  {g.label}{g.note && <span className="text-gold normal-case tracking-normal font-serif italic text-sm ml-3">{g.note}</span>}
                </p>
                <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {items.map((s, i) => (
                    <FadeUp key={s.service_id} delay={(i % 3) * 0.1}
                      className={`trace-card rounded-3xl p-8 flex flex-col ${g.key === "package" ? "bg-obsidian text-white grain relative overflow-hidden border border-[#D4AF37]/30" : "border border-purple-100 bg-white"}`}
                      data-testid={`service-card-${s.service_id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <h3 className={`font-serif text-2xl leading-snug ${g.key === "package" ? "text-white" : ""}`}>{s.name}</h3>
                        {g.key !== "individual" && (
                          <span className={`text-[10px] font-mono uppercase tracking-widest rounded-full px-2.5 py-1 shrink-0 ${
                            g.key === "package" ? "bg-gold/20 text-gold border border-gold/50" : "bg-gold/15 text-[#8a6d1d] border border-gold/40"
                          }`}>{g.key === "package" ? "Lock-In" : "Deal"}</span>
                        )}
                      </div>
                      <p className={`text-sm leading-relaxed mt-3 flex-1 ${g.key === "package" ? "text-white/60" : "text-slate-500"}`}>{s.description}</p>
                      <div className="mt-6 flex items-end justify-between">
                        <div>
                          <p className={`font-serif text-4xl ${g.key === "package" ? "gold-gradient-text" : "text-obsidian"}`} data-testid={`service-price-${s.service_id}`}>
                            ${price(s)}
                          </p>
                          <p className={`text-xs mt-1 flex items-center gap-1.5 ${g.key === "package" ? "text-white/40" : "text-slate-400"}`}>
                            {s.unit}
                            {s.duration_minutes > 0 && <><Clock size={11} /> {s.duration_minutes} min</>}
                          </p>
                          <p className={`text-[10px] font-mono uppercase tracking-widest mt-2 ${g.key === "package" ? "text-gold/80" : "text-purple-700/70"}`} data-testid={`service-deposit-${s.service_id}`}>
                            {s.deposit_percent ?? 50}% deposit to lock in
                          </p>
                        </div>
                        <Link
                          to={`/book?service=${s.service_id}&location=${locId}`}
                          data-testid={`service-book-${s.service_id}`}
                          className={`w-11 h-11 rounded-full flex items-center justify-center transition-colors ${g.key === "package" ? "bg-gold text-obsidian hover:brightness-110" : "bg-obsidian text-white hover:bg-gold hover:text-obsidian"}`}
                          aria-label={`Book ${s.name}`}
                        >
                          <ArrowRight size={16} />
                        </Link>
                      </div>
                    </FadeUp>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>
      <Marquee items={["Recording $50/hr", "Mixing $75/mix", "2-Hour Special $200", "Lock-In Packages", "Monthly Deal $500", "Pro Tools", "Studio One"]} />
    </div>
  );
}
